import { Component, computed, inject, output, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { getAddressLine1 } from '@shared/validation/address';
import { Toggle } from '@tvd/design-system/toggle';
import { AuthService } from '@tvd/data-access/auth';
import { NavDrawerUser } from '@tvd/ui/nav-drawer/nav-drawer-user';
import { BottomBarService } from '../bottom-bar.service';
import { Icon } from '@tvd/design-system/icon';
import { NotificationStore } from '@tvd/data-access/notifications';
import { AiChatDrawerService } from '@tvd/feat/chat/ai-chat/ai-chat-drawer.service';
import { IntakeDrawerService } from '@tvd/feat/intake/drawer/intake-drawer.service';
import { hasRole } from '@shared/auth/has-role';

@Component({
  selector: 'bottom-bar-menu',
  imports: [RouterLink, Toggle, TranslatePipe, NavDrawerUser, Icon],
  host: {
    class: 'block w-full',
    '(click)': '$event.stopPropagation()',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="bg-card max-h-[calc(100dvh-120px)] overflow-y-auto">
      <nav class="p-4">
        <div>
          <h2 class="text-lg font-bold">
            {{ selectedBranch()?.name }}
          </h2>
          <p class="text-sm">
            {{ selectedBranchLine1() }},
            {{ selectedBranch()?.address?.city }}
            {{ selectedBranch()?.address?.zip }}
          </p>
        </div>
        <ul class="space-y-1 mt-4">
          <li>
            <a
              [routerLink]="[
                '/dashboard',
                selectedBranchOrgId(),
                selectedBranchId(),
                'staff',
              ]"
              (click)="
                $event.stopPropagation(); $event.preventDefault(); close.emit()
              "
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
            >
              <tvd-icon
                icon="person"
                class="text-foreground/80 group-hover:text-primary transition-colors"
              />
              <span class="text-sm font-medium">{{
                'enum.entityType.staff' | translate
              }}</span>
            </a>
          </li>
          <li>
            <a
              [routerLink]="[
                '/dashboard',
                selectedBranchOrgId(),
                selectedBranchId(),
                'customer',
              ]"
              (click)="
                $event.stopPropagation(); $event.preventDefault(); close.emit()
              "
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
            >
              <tvd-icon
                icon="frame_person"
                class="text-foreground/80 group-hover:text-primary transition-colors"
              />
              <span class="text-sm font-medium">{{
                'navigation.customers' | translate
              }}</span>
            </a>
          </li>

          <li>
            <a
              [routerLink]="[
                '/dashboard',
                selectedBranchOrgId(),
                selectedBranchId(),
                'services',
              ]"
              (click)="
                $event.stopPropagation(); $event.preventDefault(); close.emit()
              "
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
            >
              <tvd-icon
                icon="design_services"
                class="text-foreground/80 group-hover:text-primary transition-colors"
              />
              <span class="text-sm font-medium">{{
                'label.services' | translate
              }}</span>
            </a>
          </li>

          <li>
            <a
              [routerLink]="[
                '/dashboard',
                selectedBranchOrgId(),
                selectedBranchId(),
                'request',
              ]"
              (click)="
                $event.stopPropagation(); $event.preventDefault(); close.emit()
              "
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
            >
              <tvd-icon
                icon="description"
                class="text-foreground/80 group-hover:text-primary transition-colors"
              />
              <span class="text-sm font-medium">{{
                'navigation.requests' | translate
              }}</span>
            </a>
          </li>

          <li>
            <a
              [routerLink]="[
                '/dashboard',
                selectedBranchOrgId(),
                selectedBranchId(),
                'vouchers',
              ]"
              (click)="
                $event.stopPropagation(); $event.preventDefault(); close.emit()
              "
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
            >
              <tvd-icon
                icon="confirmation_number"
                class="text-foreground/80 group-hover:text-primary transition-colors"
              />
              <span class="text-sm font-medium">{{
                'navigation.vouchers' | translate
              }}</span>
            </a>
          </li>

          <li>
            <a
              [routerLink]="[
                '/dashboard',
                selectedBranchOrgId(),
                selectedBranchId(),
                'products',
              ]"
              (click)="
                $event.stopPropagation(); $event.preventDefault(); close.emit()
              "
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
            >
              <tvd-icon
                icon="inventory_2"
                class="text-foreground/80 group-hover:text-primary transition-colors"
              />
              <span class="text-sm font-medium">{{
                'navigation.products' | translate
              }}</span>
            </a>
          </li>

          <li>
            <a
              [routerLink]="[
                '/dashboard',
                selectedBranchOrgId(),
                selectedBranchId(),
                'resources',
              ]"
              (click)="
                $event.stopPropagation(); $event.preventDefault(); close.emit()
              "
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
            >
              <tvd-icon
                icon="meeting_room"
                class="text-foreground/80 group-hover:text-primary transition-colors"
              />
              <span class="text-sm font-medium">{{
                'navigation.resources' | translate
              }}</span>
            </a>
          </li>

          <li>
            <a
              [routerLink]="[
                '/dashboard',
                selectedBranchOrgId(),
                selectedBranchId(),
                'memberships',
              ]"
              (click)="
                $event.stopPropagation(); $event.preventDefault(); close.emit()
              "
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
            >
              <tvd-icon
                icon="card_membership"
                class="text-foreground/80 group-hover:text-primary transition-colors"
              />
              <span class="text-sm font-medium">{{
                'navigation.memberships' | translate
              }}</span>
            </a>
          </li>

          @if (isOwner()) {
            <li>
              <a
                [routerLink]="[
                  '/dashboard',
                  selectedBranchOrgId(),
                  selectedBranchId(),
                  'reports',
                ]"
                (click)="
                  $event.stopPropagation();
                  $event.preventDefault();
                  close.emit()
                "
                class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
              >
                <tvd-icon
                  icon="analytics"
                  class="text-foreground/80 group-hover:text-primary transition-colors"
                />
                <span class="text-sm font-medium">{{
                  'label.reports' | translate
                }}</span>
              </a>
            </li>
          }

          @if (showIntake()) {
            <li>
              <button
                type="button"
                (click)="
                  $event.stopPropagation(); openIntake(); close.emit()
                "
                class="flex items-center gap-3 px-4 py-2.5 rounded-lg w-full
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
              >
                <tvd-icon
                  icon="handshake"
                  class="text-foreground/80 group-hover:text-primary transition-colors"
                />
                <span class="text-sm font-medium">{{
                  'navigation.intake' | translate
                }}</span>
              </button>
            </li>
          }

          <li>
            <div class="h-px bg-card-border my-2"></div>
          </li>

          <li>
            <div
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground transition-colors"
            >
              <div class="flex items-center justify-between w-full">
                <div class="flex items-center gap-2.5">
                  <tvd-icon icon="sync_alt" class="text-foreground/80" />
                  <span class="text-sm font-medium">{{
                    'label.leftHandMode' | translate
                  }}</span>
                </div>

                <div (click)="$event.stopPropagation()">
                  <tvd-toggle
                    [checked]="leftHandMode()"
                    (checkedChange)="handleToggleChange($event)"
                  />
                </div>
              </div>
            </div>
          </li>

          <li>
            <a
              [routerLink]="[
                '/dashboard',
                selectedBranchOrgId(),
                selectedBranchId(),
                'settings',
              ]"
              (click)="
                $event.stopPropagation(); $event.preventDefault(); close.emit()
              "
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
            >
              <div class="flex items-center gap-2.5">
                <tvd-icon
                  icon="settings"
                  class="text-foreground/80 group-hover:text-primary transition-colors"
                />
                <span class="text-sm font-medium">{{
                  'common.settings' | translate
                }}</span>
              </div>
            </a>
          </li>

          <li>
            <button
              type="button"
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg w-full
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
              (click)="$event.stopPropagation(); openAiChat(); close.emit()"
            >
              <tvd-icon
                icon="smart_toy"
                class="text-foreground/80 group-hover:text-primary transition-colors"
              />
              <span class="text-sm font-medium">AI Assistant</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              class="flex items-center gap-3 px-4 py-2.5 rounded-lg w-full
                     text-foreground hover:bg-background-50
                     focus:outline-none
                     transition-colors group"
              (click)="
                $event.stopPropagation();
                notificationStore.openDrawer();
                close.emit()
              "
            >
              <div class="relative">
                <tvd-icon
                  icon="notifications"
                  class="text-foreground/80 group-hover:text-primary transition-colors"
                />
                @if (notificationStore.unreadCount(); as count) {
                  <span
                    class="absolute -top-1 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5 leading-none"
                  >
                    {{ count > 99 ? '99+' : count }}
                  </span>
                }
              </div>
              <span class="text-sm font-medium">Notifications</span>
            </button>
          </li>

          @if (auth.user.value(); as u) {
            <li class="px-4 py-2.5">
              <tvd-nav-drawer-user [user]="u" (logout)="auth.logout()" />
            </li>
          }
        </ul>
      </nav>
    </div>
  `,
})
export class BottomBarMenuComponent {
  readonly #bottomBarService = inject(BottomBarService);
  readonly leftHandMode = this.#bottomBarService.leftHandMode;
  readonly close = output<void>();

  readonly auth = inject(AuthService);
  readonly notificationStore = inject(NotificationStore);
  readonly #aiChatDrawerService = inject(AiChatDrawerService);
  readonly #intakeDrawerService = inject(IntakeDrawerService);

  readonly router = inject(Router);
  readonly selectedBranchOrgId = this.#bottomBarService.selectedBranchOrgId;
  readonly selectedBranchId = this.#bottomBarService.selectedBranchId;
  readonly selectedBranch = this.#bottomBarService.selectedBranch;
  readonly selectedBranchLine1 = computed(() =>
    getAddressLine1(this.selectedBranch()?.address),
  );

  readonly showIntake = computed(
    () => (this.auth.user.value()?.intakeMode ?? 'NONE') !== 'NONE',
  );

  readonly isOwner = computed(() => {
    const user = this.auth.user.value();
    const orgId = this.selectedBranchOrgId();
    const branchId = this.selectedBranchId();
    return user && orgId && branchId && hasRole(user, 'owner', orgId, branchId);
  });

  openAiChat() {
    const orgId = this.selectedBranchOrgId();
    const shopId = this.selectedBranchId();
    if (orgId && shopId) {
      this.#aiChatDrawerService.open({ orgId, shopId });
    }
  }

  openIntake() {
    const orgId = this.selectedBranchOrgId();
    const branchId = this.selectedBranchId();
    const staffId = this.auth.user.value()?.id;
    if (orgId && branchId && staffId) {
      void this.#intakeDrawerService.open({ orgId, branchId, staffId });
    }
  }

  handleToggleChange(value: boolean | null) {
    if (value !== null) {
      this.#bottomBarService.toggleLeftHandMode();
    }
  }
}
